/**
 * Desktop PDP (≥1025px): NORMAL ⇄ PINNED ⇄ ABSOLUTE_SCROLLING ⇄ RELEASED.
 * Document coords: anchorAbs, galleryEndAbs; release via anchorAbs >= galleryEndAbs only.
 */
(function () {
  'use strict';

  var MQ = '(min-width: 1025px)';
  var BOTTOM_OFFSET = 24;
  /** Smoothing duration for the ABSOLUTE_SCROLLING → NORMAL transition. */
  var SETTLE_MS = 200;
  /**
   * Temporary: set to 9999 (number) to effectively disable scroll fallback.
   * null = derive from frozenBuyHeight (large distance, not a flat px constant).
   */
  var MIN_SCROLL_UP_ABSOLUTE_TO_NORMAL_OVERRIDE = null;

  /** Max viewport delta between placeholder top and absolute buy top to treat as “near natural slot”. */
  function visuallyNearNaturalSlotPx(h) {
    return Math.min(32, Math.max(12, (h || 0) * 0.04));
  }

  /** Min scroll-up from ABSOLUTE_SCROLLING entry before fallback may fire (scaled to buy box height). */
  function minScrollUpAbsoluteToNormalPx() {
    if (MIN_SCROLL_UP_ABSOLUTE_TO_NORMAL_OVERRIDE != null) {
      return MIN_SCROLL_UP_ABSOLUTE_TO_NORMAL_OVERRIDE;
    }
    var h = frozenBuyHeight || 0;
    return Math.max(h * 0.9, Math.min(420, h * 0.75));
  }

  /**
   * ABSOLUTE_SCROLLING → NORMAL is gated here.
   * Set to false to diagnose (buy stays absolute while scrolling up).
   */
  var ENABLE_ABSOLUTE_TO_NORMAL = true;

  var mql = window.matchMedia(MQ);
  var state = 'normal';
  var placeholder = null;
  var frozenBuyHeight = 0;
  var frozenBuyLeftPx = 0;
  var frozenBuyWidthPx = 0;
  /** Stored `absoluteTop` (px) when entering ABSOLUTE_SCROLLING — stable vs parsing style each tick. */
  var absoluteScrollTopPx = 0;
  /** window.scrollY snapshot when entering ABSOLUTE_SCROLLING (scroll-up delta → NORMAL fallback). */
  var scrollAtAbsoluteEnter = 0;
  /** setTimeout handle for the in-flight ABSOLUTE_SCROLLING → NORMAL settle animation. */
  var settleTimer = null;
  var lastScrollY = 0;
  var rafScheduled = false;
  var active = false;

  var section = null;
  var gallery = null;
  var rightColumn = null;
  var buyBox = null;

  function cacheElements() {
    section = document.querySelector('.productView-top');
    gallery = section ? section.querySelector('.halo-productView-left') : null;
    rightColumn = section ? section.querySelector('.halo-productView-right') : null;
    buyBox = rightColumn ? rightColumn.querySelector(':scope > .productView-product') : null;
  }

  function anchorAbsFromScroll(scrollY) {
    return scrollY + window.innerHeight - BOTTOM_OFFSET;
  }

  function galleryEndAbs(scrollY, galleryEl, fallbackEl) {
    var el = galleryEl || fallbackEl;
    if (!el) return 0;
    var r = el.getBoundingClientRect();
    return r.top + scrollY + el.scrollHeight;
  }

  function rightStartAbs(scrollY, col) {
    if (!col) return 0;
    return col.getBoundingClientRect().top + scrollY;
  }

  function clearBuyBoxInline(el) {
    if (!el) return;
    /* Clear transition first so subsequent style resets are instant, never animated. */
    el.style.transition = '';
    el.style.position = '';
    el.style.top = '';
    el.style.bottom = '';
    el.style.left = '';
    el.style.right = '';
    el.style.width = '';
  }

  function applyPinnedInline(el, col) {
    var h = frozenBuyHeight || el.offsetHeight;
    var viewportH = window.innerHeight;
    el.style.top = viewportH - h - BOTTOM_OFFSET + 'px';
    el.style.left = frozenBuyLeftPx + 'px';
    el.style.width = frozenBuyWidthPx + 'px';
  }

  function removePlaceholder() {
    if (placeholder && placeholder.parentNode) {
      placeholder.parentNode.removeChild(placeholder);
    }
    placeholder = null;
  }

  function ensurePlaceholder(heightPx) {
    if (placeholder && placeholder.parentNode) return;
    if (!buyBox || !rightColumn) return;
    var h = heightPx || frozenBuyHeight || buyBox.offsetHeight;
    var ph = document.createElement('div');
    ph.className = 'productView-product-pin-placeholder';
    ph.setAttribute('aria-hidden', 'true');
    ph.style.height = h + 'px';
    rightColumn.insertBefore(ph, buyBox);
    placeholder = ph;
  }

  function enterPinnedFromNormal() {
    if (!buyBox || !rightColumn) return;
    frozenBuyHeight = buyBox.offsetHeight;
    var buyRectCapture = buyBox.getBoundingClientRect();
    frozenBuyLeftPx = buyRectCapture.left;
    frozenBuyWidthPx = buyRectCapture.width;
    ensurePlaceholder(frozenBuyHeight);
    buyBox.classList.remove('is-released', 'is-absolute-scrolling');
    clearBuyBoxInline(buyBox);
    buyBox.classList.add('is-pinned');
    applyPinnedInline(buyBox, rightColumn);
    state = 'pinned';
  }

  function enterPinnedFromReleased() {
    if (!buyBox || !rightColumn) return;
    ensurePlaceholder(frozenBuyHeight);
    var buyRectCapture = buyBox.getBoundingClientRect();
    frozenBuyLeftPx = buyRectCapture.left;
    frozenBuyWidthPx = buyRectCapture.width;
    buyBox.classList.remove('is-released', 'is-absolute-scrolling');
    clearBuyBoxInline(buyBox);
    buyBox.classList.add('is-pinned');
    applyPinnedInline(buyBox, rightColumn);
    state = 'pinned';
  }

  function enterAbsoluteScrolling() {
    if (!buyBox || !rightColumn || !placeholder) return;

    var scrollY = window.scrollY;
    var viewportH = window.innerHeight;
    var rsAbs = rightStartAbs(scrollY, rightColumn);
    var fixedTopAbs = scrollY + (viewportH - frozenBuyHeight - BOTTOM_OFFSET);
    var absoluteTop = fixedTopAbs - rsAbs;

    var rightRectCap = rightColumn.getBoundingClientRect();
    var buyRectCap = buyBox.getBoundingClientRect();
    var relativeLeft = buyRectCap.left - rightRectCap.left;
    var buyWidthCap = buyRectCap.width;

    buyBox.classList.remove('is-pinned');
    clearBuyBoxInline(buyBox);
    buyBox.classList.add('is-absolute-scrolling');
    buyBox.style.position = 'absolute';
    buyBox.style.top = absoluteTop + 'px';
    buyBox.style.left = relativeLeft + 'px';
    buyBox.style.right = 'auto';
    buyBox.style.width = buyWidthCap + 'px';
    buyBox.style.bottom = 'auto';

    absoluteScrollTopPx = absoluteTop;
    scrollAtAbsoluteEnter = scrollY;

    state = 'absoluteScrolling';
  }

  /**
   * Smoothly animate the absolute buy box to the placeholder's natural slot,
   * then return to NORMAL. Eliminates the visible δ-pixel jump caused by the
   * single-frame ABSOLUTE_SCROLLING → NORMAL switch.
   */
  function startSettling() {
    if (!buyBox || !rightColumn || !placeholder) return;

    var scrollY = window.scrollY;
    var rsAbs = rightStartAbs(scrollY, rightColumn);
    var phTopAbs = placeholder.getBoundingClientRect().top + scrollY;
    var targetTop = phTopAbs - rsAbs;

    buyBox.style.transition = 'top ' + SETTLE_MS + 'ms ease-out';
    /* Force a reflow so the browser registers the current top before we change it. */
    void buyBox.offsetWidth;
    buyBox.style.top = targetTop + 'px';
    absoluteScrollTopPx = targetTop;

    state = 'settling';

    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = setTimeout(function () {
      settleTimer = null;
      if (state === 'settling') {
        leaveToNormal();
      }
    }, SETTLE_MS + 30);
  }

  function leaveToNormal() {
    if (!buyBox) return;
    if (settleTimer) {
      clearTimeout(settleTimer);
      settleTimer = null;
    }
    buyBox.classList.remove('is-pinned', 'is-released', 'is-absolute-scrolling');
    clearBuyBoxInline(buyBox);
    removePlaceholder();
    frozenBuyHeight = 0;
    frozenBuyLeftPx = 0;
    frozenBuyWidthPx = 0;
    absoluteScrollTopPx = 0;
    scrollAtAbsoluteEnter = 0;
    state = 'normal';
    cacheElements();
  }

  function enterReleased() {
    if (!buyBox || !rightColumn) return;
    ensurePlaceholder(frozenBuyHeight);

    var scrollY = window.scrollY;
    var endAbs = galleryEndAbs(scrollY, gallery, section);
    var rStart = rightStartAbs(scrollY, rightColumn);

    var rightRectCap = rightColumn.getBoundingClientRect();
    var buyRectCap = buyBox.getBoundingClientRect();
    var relativeLeft = buyRectCap.left - rightRectCap.left;
    var buyWidthCap = buyRectCap.width;

    buyBox.classList.remove('is-pinned', 'is-absolute-scrolling');
    clearBuyBoxInline(buyBox);

    var topPx = endAbs - rStart - frozenBuyHeight;
    buyBox.style.position = 'absolute';
    buyBox.style.top = topPx + 'px';
    buyBox.style.left = relativeLeft + 'px';
    buyBox.style.width = buyWidthCap + 'px';
    buyBox.style.bottom = 'auto';
    buyBox.style.right = 'auto';

    buyBox.classList.add('is-released');
    absoluteScrollTopPx = 0;
    scrollAtAbsoluteEnter = 0;
    state = 'released';
  }

  function resetDom() {
    if (settleTimer) {
      clearTimeout(settleTimer);
      settleTimer = null;
    }
    cacheElements();
    if (buyBox) {
      buyBox.classList.remove('is-pinned', 'is-released', 'is-absolute-scrolling');
      clearBuyBoxInline(buyBox);
    }
    removePlaceholder();
    frozenBuyHeight = 0;
    frozenBuyLeftPx = 0;
    frozenBuyWidthPx = 0;
    absoluteScrollTopPx = 0;
    scrollAtAbsoluteEnter = 0;
    state = 'normal';
    section = null;
    gallery = null;
    rightColumn = null;
    buyBox = null;
  }

  function tick() {
    if (!mql.matches) {
      disable();
      return;
    }
    cacheElements();
    if (!section || !rightColumn || !buyBox) return;

    /* Skip the whole sticky system when the gallery is not strictly taller
       than the buy box: there is no scroll headroom to "follow" — pinning
       would only push the buy box behind the header on scroll up. */
    var galleryH = gallery ? gallery.offsetHeight : 0;
    var buyH = buyBox.offsetHeight;
    if (galleryH <= buyH + BOTTOM_OFFSET) {
      if (state !== 'normal') {
        leaveToNormal();
      }
      return;
    }

    var scrollY = window.scrollY;
    var viewportH = window.innerHeight;
    var direction =
      scrollY > lastScrollY ? 'down' : scrollY < lastScrollY ? 'up' : null;
    lastScrollY = scrollY;

    var anchorAbs = anchorAbsFromScroll(scrollY);
    var endAbs = galleryEndAbs(scrollY, gallery, section);

    if (state !== 'normal') {
      if (!placeholder || !placeholder.parentNode) {
        leaveToNormal();
        return;
      }
      var naturalBottomAbs =
        rightStartAbs(scrollY, rightColumn) + frozenBuyHeight;
      if (anchorAbs <= naturalBottomAbs) {
        leaveToNormal();
        return;
      }
    }

    if (state === 'normal') {
      if (direction === 'down') {
        var buyRect = buyBox.getBoundingClientRect();
        var buyBottomAbs = buyRect.bottom + scrollY;
        if (buyBottomAbs <= anchorAbs) {
          enterPinnedFromNormal();
          if (anchorAbs >= endAbs) {
            enterReleased();
          }
        }
      }
      return;
    }

    if (state === 'pinned') {
      if (!placeholder || !placeholder.parentNode) {
        leaveToNormal();
        return;
      }

      if (anchorAbs >= endAbs) {
        enterReleased();
        return;
      }

      applyPinnedInline(buyBox, rightColumn);
      return;
    }

    if (state === 'absoluteScrolling') {
      if (!placeholder || !placeholder.parentNode) {
        leaveToNormal();
        return;
      }

      if (anchorAbs >= endAbs) {
        enterReleased();
        return;
      }

      if (!ENABLE_ABSOLUTE_TO_NORMAL) {
        return;
      }

      var phRect = placeholder.getBoundingClientRect();
      var buyRect = buyBox.getBoundingClientRect();

      var topGapPx = Math.abs(phRect.top - buyRect.top);
      var nearSlot =
        topGapPx <= visuallyNearNaturalSlotPx(frozenBuyHeight);

      if (direction === 'up' && nearSlot) {
        startSettling();
        return;
      }

      return;
    }

    if (state === 'settling') {
      /* Animation owns the buy box; only allow release if section ends meanwhile. */
      if (anchorAbs >= endAbs) {
        if (settleTimer) {
          clearTimeout(settleTimer);
          settleTimer = null;
        }
        enterReleased();
      }
      return;
    }

    if (state === 'released') {
      if (direction === 'up' && anchorAbs < endAbs) {
        enterPinnedFromReleased();
      }
      return;
    }
  }

  function scheduleTick() {
    if (rafScheduled) return;
    rafScheduled = true;
    requestAnimationFrame(function () {
      rafScheduled = false;
      tick();
    });
  }

  function enable() {
    if (active) return;
    active = true;
    cacheElements();
    window.addEventListener('scroll', scheduleTick, { passive: true });
    window.addEventListener('resize', scheduleTick);
    lastScrollY = window.scrollY;
    scheduleTick();
  }

  function disable() {
    if (!active) return;
    active = false;
    window.removeEventListener('scroll', scheduleTick);
    window.removeEventListener('resize', scheduleTick);
    resetDom();
  }

  function onMqChange() {
    if (mql.matches) {
      enable();
    } else {
      disable();
    }
  }

  if (mql.addEventListener) {
    mql.addEventListener('change', onMqChange);
  } else if (mql.addListener) {
    mql.addListener(onMqChange);
  }

  function init() {
    if (mql.matches) enable();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
